import { localize } from "@/i18n/translate";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertCircle, CheckCircle2, Loader2, Mail } from "lucide-react";
import { CONTACT } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { useContent } from "@/i18n/LangContext";
const API = "/api";
const TOPICS = ["Leistungen", "Mitgliedschaft", "Kundenbereich", "Sonstiges"];
const EMPTY = {
  name: "",
  email: "",
  phone: "",
  topic: "Leistungen",
  message: "",
  consent: false,
  website: ""
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const validate = (v, m) => {
  const e = {};
  if (v.name.trim().length < 2) e.name = m.name;
  if (!EMAIL_RE.test(v.email.trim())) e.email = m.email;
  const digits = v.phone.replace(/\D/g, "");
  if (!/^[+\d\s().-]+$/.test(v.phone.trim()) || digits.length < 7 || digits.length > 15) e.phone = m.phone;
  if (v.message.trim().length < 20) e.message = m.message;
  if (!v.consent) e.consent = m.consent;
  return e;
};
export const buildMailto = (v, f) => {
  const subject = `${f.mailSubject}: ${f.topics[v.topic]}`;
  const body = [v.message || f.mailBodyPlaceholder, "", "—", v.name && `${f.name}: ${v.name}`, v.phone && `${f.phone}: ${v.phone}`, v.email && `${f.emailL}: ${v.email}`].filter(x => x !== false && x !== undefined && x !== "").join("\n");
  return `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};
const Field = ({
  id,
  label,
  error,
  optional,
  children
}) => {
  const {
    ui
  } = useContent();
  return <div>
    <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-ink">
      {localize(label)}
      <span className="font-mono text-xs font-normal text-mute">{localize(optional ? ui.form.optional : ui.form.required)}</span>
    </label>
    {localize(children)}
    {localize(error && <p id={`${id}-error`} data-testid={`${id}-error`} className="mt-2 flex items-start gap-2 text-sm text-signaldark">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        {localize(error)}
      </p>)}
  </div>;
};
const inputCls = err => `mt-2 block w-full border-0 border-b bg-transparent px-0 py-3 text-base text-ink placeholder:text-zinc-500 focus:outline-none focus-visible:outline-none focus:ring-0 transition-colors ${err ? "border-signal" : "border-ink/30 focus:border-ink"} focus-visible:border-signal focus-visible:shadow-[0_1px_0_0_#D52B1E]`;
export const ContactForm = () => {
  const [v, setV] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState({
    state: "idle"
  });
  const formRef = useRef(null);
  const {
    ui,
    lang
  } = useContent();
  const f = ui.form;
  useEffect(() => {
    if (touched) setErrors(validate(v, f.err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  useEffect(() => {
    const onTopic = e => setV(p => ({
      ...p,
      topic: TOPICS.includes(e.detail) ? e.detail : p.topic
    }));
    window.addEventListener("sss:topic", onTopic);
    return () => window.removeEventListener("sss:topic", onTopic);
  }, []);
  const set = k => e => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const next = {
      ...v,
      [k]: val
    };
    setV(next);
    if (touched) setErrors(validate(next, f.err));
  };
  const ariaFor = k => ({
    "aria-invalid": errors[k] ? true : undefined,
    "aria-describedby": errors[k] ? `${k}-error` : undefined
  });
  const onSubmit = async e => {
    e.preventDefault();
    setTouched(true);
    const errs = validate(v, f.err);
    setErrors(errs);
    if (Object.keys(errs).length) {
      const first = Object.keys(errs)[0];
      formRef.current?.querySelector(`#${first}`)?.focus();
      setStatus({
        state: "invalid"
      });
      return;
    }
    if (!API) {
      setStatus({
        state: "error",
        msg: "unavailable"
      });
      return;
    }
    setStatus({
      state: "sending"
    });
    try {
      const res = await axios.post(`${API}/contact`, {
        ...v,
        name: v.name.trim(),
        email: v.email.trim(),
        phone: v.phone.trim()
      }, {
        timeout: 20000
      });
      setStatus({
        state: "success",
        ref: res.data?.id ?? ""
      });
      setV(EMPTY);
      setTouched(false);
      setTimeout(() => scrollToId("contact-success"), 50);
    } catch (err) {
      setStatus({
        state: "error",
        msg: err?.response?.status === 422 ? "invalidData" : "failed"
      });
    }
  };
  if (status.state === "success") {
    return <div id="contact-success" role="status" data-testid="contact-success" className="border-t-2 border-signal bg-white/60 p-8 md:p-10">
        <CheckCircle2 className="h-8 w-8 text-signal" aria-hidden="true" />
        <h3 className="mt-5 font-display text-2xl font-bold">{localize(f.successTitle)}</h3>
        <p className="mt-3 leading-relaxed text-mute">
          {localize(f.successText)}{localize(status.ref && <> {localize(f.ref)} <span className="font-mono text-ink">{localize(status.ref)}</span></>)}
        </p>
        <button type="button" onClick={() => setStatus({
        state: "idle"
      })} data-testid="contact-new-request" className="mt-8 border-b border-ink pb-1 text-sm font-medium hover:border-signal hover:text-signal">
          {localize(f.another)}
        </button>
      </div>;
  }
  const errCount = Object.keys(errors).length;
  return <form ref={formRef} onSubmit={onSubmit} noValidate data-testid="contact-form" className="space-y-8" aria-describedby="form-hint">
      <p id="form-hint" className="text-sm text-mute">{localize(f.hint)}</p>
      <div aria-live="polite" className="empty:hidden">
        {localize(status.state === "invalid" && errCount > 0 && <p data-testid="contact-error-summary" className="flex items-start gap-2 border-l-2 border-signal bg-signal/5 p-4 text-sm text-signaldark">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {localize(errCount === 1 ? f.checkOne : f.checkMany(errCount))}
          </p>)}
        {localize(status.state === "error" && <div data-testid="contact-submit-error" className="border-l-2 border-signal bg-signal/5 p-4 text-sm text-ink">
            <p className="font-medium text-signaldark">{localize(f[status.msg])} {localize(f.kept)}</p>
            <p className="mt-1 text-mute">{localize(f.altHint)}</p>
            <a href={buildMailto(v, f)} data-testid="contact-error-mailto" className="mt-3 inline-flex items-center gap-2 font-medium text-ink underline underline-offset-4 hover:text-signal">
              <Mail className="h-4 w-4" aria-hidden="true" /> {localize(f.prepare)}
            </a>
          </div>)}
      </div>
      <div className="grid gap-8 sm:grid-cols-2">
        <Field id="name" label={f.name} error={errors.name}>
          <input id="name" data-testid="contact-name-input" autoComplete="name" value={v.name} onChange={set("name")} className={inputCls(errors.name)} {...ariaFor("name")} />
        </Field>
        <Field id="email" label={f.emailL} error={errors.email}>
          <input id="email" type="email" data-testid="contact-email-input" autoComplete="email" value={v.email} onChange={set("email")} className={inputCls(errors.email)} {...ariaFor("email")} />
        </Field>
        <Field id="phone" label={f.phone} error={errors.phone}>
          <input id="phone" type="tel" required maxLength={40} data-testid="contact-phone-input" autoComplete="tel" value={v.phone} onChange={set("phone")} className={inputCls(errors.phone)} {...ariaFor("phone")} />
        </Field>
        <Field id="topic" label={f.topic}>
          <select id="topic" data-testid="contact-topic-select" value={v.topic} onChange={set("topic")} className={`${inputCls(false)} cursor-pointer`}>
            {localize(TOPICS.map(t => <option key={t} value={t}>{localize(f.topics[t])}</option>))}
          </select>
        </Field>
      </div>
      <Field id="message" label={f.message} error={errors.message}>
        <textarea id="message" rows={5} data-testid="contact-message-input" placeholder={localize(f.placeholder)} value={v.message} onChange={set("message")} className={`${inputCls(errors.message)} resize-y`} {...ariaFor("message")} />
      </Field>
      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">{localize("Website")}</label>
        <input id="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set("website")} />
      </div>
      <div>
        <label htmlFor="consent" className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink">
          <input id="consent" type="checkbox" data-testid="contact-consent-checkbox" checked={v.consent} onChange={set("consent")} className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer accent-[#D52B1E]" {...ariaFor("consent")} />
          <span>
            {localize(f.consentA)}<Link to="/datenschutz" className="underline underline-offset-4 hover:text-signal" data-testid="contact-privacy-link">{localize(f.consentLink)}</Link>{localize(f.consentB)}
          </span>
        </label>
        {localize(errors.consent && <p id="consent-error" data-testid="consent-error" className="mt-2 flex items-start gap-2 text-sm text-signaldark">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            {localize(errors.consent)}
          </p>)}
      </div>
      <div className="flex flex-col gap-5 border-t border-ink/15 pt-8 sm:flex-row sm:items-center sm:justify-between">
        <button type="submit" disabled={status.state === "sending"} data-testid="contact-submit-btn" className="inline-flex items-center justify-center gap-3 bg-signal px-8 py-4 font-medium text-white transition-colors hover:bg-signaldark disabled:opacity-70">
          {localize(status.state === "sending" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />)}
          {localize(status.state === "sending" ? f.sending : f.submit)}
        </button>
        <a href={buildMailto(v, f)} data-testid="contact-mailto-btn" className="inline-flex items-center gap-2 text-sm font-medium text-ink underline-offset-4 hover:text-signal hover:underline">
          <Mail className="h-4 w-4" aria-hidden="true" />
          {localize(f.mailto)}
        </a>
      </div>
    </form>;
};
